import {
  MAX_PAGE_SIZE,
  type DiscountRule,
  type DiscountRulesListQuerySchema,
  type Paginated,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { type SelectionOption } from '@/shared/components/selection/types/selection-option.types';
import {
  type ServerSelectQueryArgs,
  type UseServerSelectQuery,
  type UseServerSelectResolve,
} from '@/shared/components/selection/types/server-selection.types';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/**
 * The query as it goes ON THE WIRE — the schema's INPUT, not its output.
 *
 * `ids` is the difference: the contract parses one comma-joined parameter into
 * an array, so the parsed type is `string[]` while the thing to send is
 * `'a,b,c'`. Typing the request by the output would make RTK Query serialize
 * `?ids=a&ids=b`, which `qs` hands the api as an array — exactly the ambiguity
 * `IdsParamSchema` exists to remove (N7).
 */
type DiscountRuleLookupParams = z.input<typeof DiscountRulesListQuerySchema>;

/**
 * The **cross-module read bridge** for discount rules (W6).
 *
 * ## Why a bridge and not an import
 *
 * The rules belong to `modules/Courses`; the offer-code screen belongs to
 * `modules/Subscriptions`. A module never imports another's api (W6), so the
 * shared read lives here — read-only on purpose, because authoring a rule is the
 * courses module's job and always will be.
 *
 * ## It needs a permission the screens that use it may not have
 *
 * `GET /admin/discount-rules` is **`courses.read`**, not `billing.read`. A
 * caller therefore checks `can('courses.read')` and **withholds the control**
 * rather than mounting a picker guaranteed to 403 (P21b) — hiding a request is
 * cheaper and more honest than making one that cannot succeed.
 *
 * Kept in step with the rules editor through the client bus (W5): see
 * `lookup-invalidation`, which invalidates the tag on `courses.rule.changed`.
 */
export const discountRuleLookupApi = createApi({
  reducerPath: 'discountRuleLookupApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['DiscountRuleLookup'],
  endpoints: (builder) => ({
    lookupDiscountRules: builder.query<Paginated<DiscountRule>, DiscountRuleLookupParams>({
      query: (params) => ({ url: '/admin/discount-rules', method: 'GET', params }),
      providesTags: ['DiscountRuleLookup'],
    }),
  }),
});

export const { useLookupDiscountRulesQuery } = discountRuleLookupApi;

/**
 * The **name** an operator reads, over the **course or test** it pays for.
 *
 * `name` is staff shorthand and is deliberately not translated — it is what the
 * author typed and what `search` matches. `sourceName` is the course/test title,
 * already resolved to the caller's locale (N5), and it is what disambiguates two
 * rules whose names read alike. It is nullable, and a `null` becomes **no
 * description at all** rather than the row's uuid: an id under a label is noise
 * an operator cannot act on.
 */
function toItem(rule: DiscountRule): SelectionOption {
  return {
    value: rule.id,
    label: rule.name,
    ...(rule.sourceName === null ? {} : { description: rule.sourceName }),
  };
}

const EMPTY: readonly SelectionOption[] = [];

/** Adapts the bridge to `ServerSelect`'s option-page contract. */
export const useRuleOptionsQuery: UseServerSelectQuery = ({
  search,
  page,
  pageSize,
}: ServerSelectQueryArgs) => {
  const { data, isFetching, isError, refetch } = useLookupDiscountRulesQuery({
    page,
    pageSize,
    // Alphabetical, because a rule list has no staff-arranged order of its own
    // and the primary key is not something an operator can scan.
    sort: 'name',
    order: 'asc',
    // No `isActive` filter, deliberately: a withdrawn rule still has live store
    // codes pointing at it, and those are exactly the ones an operator has come
    // to this screen to find and edit by hand.
    ...(search === '' ? {} : { search }),
  });

  return {
    items: data === undefined ? EMPTY : data.items.map(toItem),
    hasNext: data?.meta.hasNext ?? false,
    isFetching,
    isError,
    // So a failed load offers a way out rather than a dead-end message (U0).
    refetch,
  };
};

/**
 * Resolves saved rule ids that may be on no page at all.
 *
 * A rule the api does not return comes back **absent**, deliberately: inventing
 * an option would make a rule nobody can reach selectable again. The kit's own
 * `labelOf` already falls back to the raw value for a chip, which is the right
 * layer for that fallback (P23b2).
 */
export const useResolveDiscountRuleIds: UseServerSelectResolve = (ids: readonly string[]) => {
  const { data, isFetching } = useLookupDiscountRulesQuery(
    {
      page: 1,
      // Clamped, because the api REFUSES an over-large page rather than clamping
      // it (N7) — and `IdsParamSchema` caps the parameter at the same ceiling.
      pageSize: Math.min(Math.max(ids.length, 1), MAX_PAGE_SIZE),
      sort: 'name',
      order: 'asc',
      ids: ids.join(','),
    },
    // Nothing to resolve is not an empty request — it is no request.
    { skip: ids.length === 0 },
  );

  return {
    items: data === undefined ? EMPTY : data.items.map(toItem),
    isFetching,
  };
};
